'use client'

import type { ReactNode } from 'react'
import { MapPin, Ticket, Timer } from 'lucide-react'
import { resolvePublicAssetUrl } from './publicAssetUrl'
import { DetailsFact, DetailsFactIcon, DetailsFacts, DetailsPeopleIcon } from '../shared/DetailsFact'
import { useTranslation } from '../../../hooks/useTranslation'
import type { PublicScheduleInstructor, PublicScheduleLocation } from '../../../services/public-schedule.service'

/** An offer's facts in its details window, as a Term's: who leads it, where, for how much and how long. */
export function PublicOfferSummary({
    price,
    durationMinutes,
    instructors = [],
    locations = [],
}: {
    price?: ReactNode
    durationMinutes?: number
    instructors?: PublicScheduleInstructor[]
    locations?: PublicScheduleLocation[]
}) {
    const { t } = useTranslation('landing')
    const people = instructors.flatMap((person) => {
        const name = `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim()
        return name ? [{ key: person.id, name, avatarUrl: resolvePublicAssetUrl(person.avatarUrl) }] : []
    })
    const places = locations.filter((place) => place.name?.trim())
    return (
        <DetailsFacts>
            {people.length ? (
                <DetailsFact label={t('publicEntry.details.instructors')} icon={<DetailsPeopleIcon people={people} muted={false}/>}>
                    {people.map(person => person.name).join(', ')}
                </DetailsFact>
            ) : null}
            {places.length ? (
                <DetailsFact
                    label={t(places.length > 1 ? 'publicEntry.details.availableLocations' : 'publicEntry.details.location')}
                    icon={<DetailsFactIcon muted={false}><MapPin className="h-4 w-4"/></DetailsFactIcon>}
                >
                    {places.map(place => place.name).join(', ')}
                </DetailsFact>
            ) : null}
            {price ? (
                <DetailsFact label={t('publicEntry.events.price')} icon={<DetailsFactIcon muted={false}><Ticket className="h-4 w-4"/></DetailsFactIcon>}>
                    {price}
                </DetailsFact>
            ) : null}
            {durationMinutes ? (
                <DetailsFact label={t('publicEntry.pricing.compact.durationColumn')} icon={<DetailsFactIcon muted={false}><Timer className="h-4 w-4"/></DetailsFactIcon>}>
                    <span className="tabular-nums">{durationMinutes} min</span>
                </DetailsFact>
            ) : null}
        </DetailsFacts>
    )
}
